import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Eye, ThumbsUp, Clock, CheckCircle, XCircle, AlertCircle, Loader } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Feedback, FeedbackStatus } from '../../types/feedback';
import { formatRelativeTime, formatViewCount } from '../../utils/formatters';

interface FeedbackListProps {
  items: Feedback[];
  type: 'bug' | 'feature' | 'feedback';
}

export default function FeedbackList({ items, type }: FeedbackListProps) {
  const { theme } = useTheme();

  const getStatusColor = (status: FeedbackStatus) => {
    switch (status) {
      case 'approved':
        return 'text-green-500';
      case 'rejected':
        return 'text-red-500';
      case 'in-progress':
        return 'text-blue-500';
      case 'completed':
        return 'text-purple-500';
      default:
        return theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
    }
  };

  const getStatusIcon = (status: FeedbackStatus) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4" />;
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      case 'in-progress':
        return <Loader className="h-4 w-4 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-4">
      {items.map(item => (
        <div
          key={item.id}
          className={`
            p-4 rounded-lg transition-colors
            ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
          `}
        >
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                <Link
                  to={`/${type}/${item.id}`}
                  className={`font-medium hover:text-blue-500 truncate ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}
                >
                  {item.title}
                </Link>
                <div className={`
                  flex items-center gap-1 px-2 py-0.5 rounded-full text-xs w-fit
                  ${getStatusColor(item.status)}
                  ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}
                `}>
                  {getStatusIcon(item.status)}
                  <span>{item.status}</span>
                </div>
              </div>

              <p className={`text-sm mb-2 line-clamp-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {item.description}
              </p>

              <div className="flex flex-wrap items-center gap-4 text-xs">
                <div className={`flex items-center gap-1 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <AlertCircle className="h-4 w-4" />
                  <span>{item.product}</span>
                </div>
                <div className={`flex items-center gap-1 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <Clock className="h-4 w-4" />
                  <span>{formatRelativeTime(item.createdAt)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 sm:flex-col sm:items-end">
              <div className={`flex items-center gap-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <ThumbsUp className="h-4 w-4" />
                <span>{formatViewCount(item.likes)}</span>
              </div>
              <div className={`flex items-center gap-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <MessageSquare className="h-4 w-4" />
                <span>{item.comments.length}</span>
              </div>
              <div className={`flex items-center gap-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <Eye className="h-4 w-4" />
                <span>{formatViewCount(item.views)}</span>
              </div>
            </div>
          </div>

          {item.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {item.tags.map(tag => (
                <span
                  key={tag}
                  className={`
                    text-xs px-2 py-0.5 rounded-full
                    ${theme === 'dark'
                      ? 'bg-gray-700 text-gray-300'
                      : 'bg-gray-100 text-gray-600'
                    }
                  `}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}